/* Серверный режим экрана «Карусель для поста» (post) на движке шаблона (план вехи 3, lane D; collage-slides §2.2).
   createPost(createPostServer) (features/post/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект:
   цена, готовность, свои фото, создание, хвост и итог берутся только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё состояние экрана (S), свой мост загрузок и свой хвост BotRun, поэтому фото, согласие и заказ одного приложения или владельца
     не видны и не уходят под другим;
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts);
   - S → входы — одна точка записи (inputs-mirror; зеркало зовут syncFoot и renderCar движка): те же ключи и кодирование, что у родной
     формы (post-inputs.ts, SlideOrderForm.tsx kind="post"); входы, изменённые не зеркалом (повтор из «Истории», заявка родной формы), — в S;
   - свои фото — лоток моста загрузок (media-bridge: слоты формы photo_1…photo_10, согласие первым, одна очередь, «Продолжить загрузку»
     тем же id, окончательный отказ убирает файл); ключи слотов пишет мост, S.photos — только его записи;
   - STY, TONES, FMTS и NS сужаются на месте до опубликованных значений (полные списки шаблона сервер помнит сам);
   - демо-лицо и демо-пользователь шаблона (BC.YOU, BC.me) в серверный режим не попадают: у стилей с фото без своих фото — нейтральный
     силуэт (D12, Q-CS11), автор слайда — пользователь сессии (BotServerMe);
   - цена на кнопке — сервиса; скидка «дешевле» — только если форма её публикует и она посчитана для этой же цены (D11);
     тексты — словарь профиля в момент показа (uiText, uiFormat). */
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance, toolPrice } from '../studio/circle/tool-helpers';
import { cNum, tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { studioAppUnit, studioCheapOffer, type StudioApp, type StudioForm } from '../studio/studio-api';
import { safeMediaUrl } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import { esc, failText, receipt, serverFoot, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createMediaBridge, mediaPolicy, type MediaBridge, type MediaTray } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService } from '../studio/engine-kit/order-service';
import { selfPicture } from '../studio/engine-kit/people-bridge';
import { archive, archiveUrl, createResultGallery, galleryKey, type GalleryItem, type GalleryLimits, type ResultGallery } from '../studio/engine-kit/result-gallery';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { createSlideEdit, type SlideEdit } from '../slides/slide-edit';
import { productDefaults, slideContractSupported } from '../slides/slide-model';
import { productResult } from '../slides/use-product-result';
import { inputsOf, photoIds, postFields, viewOf, viewOfInputs, type PostView } from './post-inputs';

/* ---------- контракт с движком (features/post/engine.ts, serve({...})) ---------- */

/** Своё фото лотка в виде шаблона; id — запись моста загрузок */
export interface PostPhoto {
  url: string;
  name: string;
  pos: number;
  f: string;
  own: boolean;
  id?: string;
}
/** Стиль шаблона (STY) */
export interface PostStyle {
  id: number | string;
  key: string;
  n: string;
  d: string;
  th: string[];
  pop: number;
  isNew: boolean;
  b: string;
  fit: string;
  demo: string;
}
/** Слайд колоды шаблона: hook — крючок, val — пункт пользы, cta — призыв */
export interface PostSlide {
  k: 'hook' | 'val' | 'cta' | string;
  kk: string;
  t: string;
  b: string;
  no?: number;
}
/** Колода шаблона (DECKS, GEN) */
export interface PostDeck {
  re: RegExp | null;
  topic: string;
  kk: string | null;
  kl: string;
  tags: string[] | null;
  items: string[][];
}
/** Состояние экрана шаблона (S), см. engine.ts §2 */
export interface PostState {
  sty: number | string;
  asty: number;
  list: Array<number | string>;
  pv: number;
  text: string;
  n: number;
  ar: string;
  tone: string;
  photos: PostPhoto[];
  ph: boolean;
  cheap: boolean;
  sh: { view: 'grid' | 'card'; card?: unknown; from?: string } | null;
  app: unknown;
  v: number;
}
/** Снимок входа заказа (r.snap шаблона, runSnap) */
export interface PostSnap {
  sty: PostStyle;
  text: string;
  n: number;
  ar: string;
  tone: string;
  photos: PostPhoto[];
  cheap: boolean;
  v: number;
  app: null;
  deck: PostSlide[];
  cap: string;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  [key: string]: unknown;
}
/** Рамка загрузки BotCore.upload экрана (UP) */
interface EngineUpload {
  o: { mb?: number; multi?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
/** Подвал BotCore.foot экрана (FOOT) */
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
/** Карточка «Ещё настройки» (BotCore.settingsCard) */
interface EngineSettings {
  sync(o?: unknown): void;
  close(): void;
  readonly isOpen?: boolean;
}
/** Крючки, которые движок отдаёт serve (engine.ts, после OWN_URL): tail, up, foot, set и rail — геттеры */
export interface PostEngineHooks {
  S: PostState;
  CAM: { st: 'idle' | 'asking' | 'on'; req: unknown; stream: unknown; sim: boolean };
  /** Стили, тоны, форматы и количества шаблона: сужаются на месте до опубликованных */
  STY: PostStyle[];
  TONES: string[][];
  FMTS: string[][];
  NS: number[];
  /** Темы листа «Все стили» (на языке профиля — на месте) */
  THEMES: string[];
  DECKS: Record<string, PostDeck>;
  GEN: PostDeck;
  TV: Record<string, { kk: string; hb: string[]; cta: string[][]; cap: string[] }>;
  CHIPS: string[][];
  readonly tail: BotRunApi;
  readonly up: EngineUpload;
  readonly foot: EngineFoot;
  readonly set: EngineSettings;
  readonly rail: { paint(): void; sync(o?: unknown): void; close(): void };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  styleOf(id: number | string): PostStyle;
  sel(): PostStyle;
  deckFor(topic: string, photos?: number): PostDeck | null;
  deckOf(deck: PostDeck, topic: string, n: number, tone: string, v: number): PostSlide[];
  hookOf(topic: string): string;
  slideHTML(st: PostStyle, sl: PostSlide, c?: Record<string, unknown>): string;
  phOf(st: PostStyle, i: number, n: number, photos?: PostPhoto[], app?: unknown): unknown;
  rsHTML(r: BotRunRun, i: number): string;
  capHTML(r: BotRunRun): string;
  runWhat(snap: unknown): string;
  setup(o: unknown): void;
  setLimits(o: { files?: number; mb?: number; tmax?: number }): void;
  setPhotos(list: PostPhoto[]): void;
  paintPage(): void;
  photosChanged(): void;
  renderCar(): void;
  railText(): void;
  syncBox(): void;
  railShow(): void;
  sheetView(view: string, card?: unknown): void;
  sheetClose(): void;
  liveStop(): void;
  camStop(paint?: boolean): void;
  edEnd(quiet?: boolean): void;
  rtBind(): void;
  runSteps(): string[][];
}
type Missing = '' | 'load' | 'off' | 'blocked' | 'unavailable';
type Ready = '' | 'upload';
/** Автор слайдов шаблона (BotCore.me): имя, ник, фото, инициалы */
export interface PostMe {
  first: string;
  last: string;
  name: string;
  user: string;
  photo: string | null;
  initials: string;
}

export interface PostServer {
  ui<U extends object>(base: U): ServerUi<U>;
  /** Текстовый шов ядра (L-TX): строки BotCore словарём профиля */
  tx(template: string, ...values: unknown[]): string;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T): T;
  missing(): Missing | null;
  ready(): Ready;
  missText(kind: string): string;
  label(): string | null;
  price(cheap: boolean): number;
  unit(): string;
  perPost(): string;
  commit(): void;
  /** Чипы темы на языке профиля: [подпись, тема] */
  chips(list: string[][]): string[][];
  /** Колода предпросмотра (null — колода шаблона) */
  prevDeck(st: PostStyle): PostSlide[] | null;
  /** «Ваше фото» у стилей с фото без своих фото: нейтральный силуэт или фото «Вы» (D12) */
  youPic(): string;
  /** Автор слайдов: пользователь сессии */
  me(): PostMe;
  upOpts(): { accept: string; tx(template: string, ...values: unknown[]): string; onPick(files: File[], at?: number): void };
  pick(files: File[]): void;
  remove(i: number): void;
  camText(): string;
  start(btn: HTMLElement): void;
  snap(): PostSnap;
  /** Слайд ленты итога: PNG сервера в рамке слайда шаблона; null — слайд в правке (разметка шаблона) */
  cell(r: BotRunRun, i: number): string | null;
  /** Правка слайда i на месте разрешена (и ячейка стала разметкой шаблона) */
  canEdit(r: BotRunRun, i: number): boolean;
  /** Правка слайда i закончилась (quiet — без отклика: другой слайд, уход, «Назад») */
  edited(r: BotRunRun, i: number, quiet: boolean): void;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  leave(go: () => void): void;
  save(btn: HTMLElement): void;
  steps(): string[][];
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
  /** «Назад» внутри экрана: правка слайда, листы и карточка настроек закрываются первыми; true — шаг сделан */
  back(): boolean;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest?: string): string };
}
const core = () => window.BotCore as unknown as Core;
const MB = 1024 * 1024;
/** «Занята» кнопка шаблона (SteosUI.busy): крутилка, пока идёт действие; уже занята — действие не начинается */
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
/** accept рамки BotCore (её проверка типа — до моста): любые фото и расширения, у которых браузер не называет тип;
    точные типы и предел — у моста по политике слота формы */
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.heic,.heif';
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Умолчания выборов: те же, что у родной формы (SlideOrderForm.tsx useProductDefaults, kind="post") */
const DEFAULTS = [
  ['slides', ['6']],
  ['format', ['4:5']],
  ['tone', ['friendly']],
  ['style', []],
] as const;
/** Тексты страницы заказа (T шаблона, engine.ts TAIL): failText берёт заголовки отказа и сбоя отсюда */
const FAIL = { modH: 'Тема не прошла проверку', techH: 'Не получилось собрать карусель', notMade: 'карусель не создана' };
const RIGHTS = { row: 'Права на свои фото', sub: 'Подтверждаю право использовать загружаемые фото' };
/** Файлы итога: слайды PNG (и JPEG, WebP) по пределам товарных экранов (slides/use-product-result.ts: до 40 файлов, 200 МБ, 120 с) */
const SLIDE_LIMITS: GalleryLimits = { limit: 40, mimes: /^image\/(jpeg|png|webp)$/, maxBytes: 200 * MB, timeoutMs: 120000 };
/** Картинка слайда сервера во всю рамку слайда шаблона (.po-sl) */
const IMG_STYLE = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block';
/** Слов в склонении: 1 слайд, 3 слайда, 5 слайдов */
function plural(value: number, forms: readonly [string, string, string]) {
  const n = Math.abs(value) % 100,
    m = n % 10;
  return forms[n > 10 && n < 20 ? 2 : m === 1 ? 0 : m >= 2 && m <= 4 ? 1 : 2];
}
/** «6 слайдов» на языке профиля: число и слово склонения (у каждого слова — свой перевод) */
export const slidesText = (n: number) => `${n} ${uiText(plural(n, ['слайд', 'слайда', 'слайдов']))}`;
/** Итог в снимке заказа: колода и подпись сервера, ключ набора (заказ:ревизия), из которого они взяты */
type ResultSnap = PostSnap & { res?: string };

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  mirror: InputsMirror;
  /** Лоток своих фото по слотам формы (photo_1…) — после первой сверки формы */
  tray: MediaTray | null;
  /** Форма, для которой прошла первая сверка (пределы, стили, тоны, форматы, количества) */
  form: StudioForm | null;
  /** Раскладка формы на экране: опубликованные стили, тоны, форматы и количества */
  layout: string;
  /** Набор id загрузок повтора, превью которого уже читали (не читать тот же набор по кругу) */
  restored: string;
  /** Ошибка формы, уже сказанная в рамке */
  said: string;
  consent: ConsentSheet | null;
  /** Форма была на экране при прошлой сверке: новый показ формы спрашивает остаток (как монтирование родной кнопки) */
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  /** Первая сверка формы (или ошибка формы): готовность первой отрисовки (EngineBound.ready) */
  settled: () => void;
  first: Promise<void>;
  driver: RunDriver;
  /** Подсмотренная при открытии заявка (до проверки владельца сервисом) */
  peek: Intent | null;
  /** S сброшен для этого кадра: входы сервиса переносятся в S при ближайшей сверке */
  fresh: boolean;
  /** Слайды итога (один набор на id:revision показанного итога) */
  gallery: ResultGallery;
  /** Правка текста слайда (slide-edit): одна на исходный готовый заказ */
  edit: { root: number; core: SlideEdit } | null;
  /** Слайды итога, которые сейчас — разметка шаблона (правка на месте, черновик, сохранение), а не PNG сервера */
  drafts: Set<number>;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createPostServer(h: PostEngineHooks): PostServer {
  const { S } = h;
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  /** Кадр, к которому относятся S и экземпляр хвоста BotRun (объект, а не ключ: ключи кадров повторяются после нового входа в раздел) */
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  const facade = {} as BotRunApi;
  /** Шаблонные значения S на новый кадр (engine.ts §2) */
  const fresh = { sty: S.sty, n: S.n, ar: S.ar, tone: S.tone };
  /** Полные списки шаблона: сужение на месте всегда идёт от них (другая форма может публиковать больше) */
  const ALL = { sty: h.STY.slice(), tones: h.TONES.slice(), fmts: h.FMTS.slice(), ns: h.NS.slice(), themes: h.THEMES.slice() };
  /** Подписи списков шаблона на языке профиля: копии (полные списки ALL остаются строками шаблона) */
  const said = {
    sty: (item: PostStyle): PostStyle => ({ ...item, n: uiText(item.n), d: uiText(item.d), th: item.th.map(theme => uiText(theme)), b: uiText(item.b), fit: uiText(item.fit) }),
    tone: ([value, label, sub]: string[]) => [value, uiText(label), uiText(sub)],
    fmt: ([value, word, where, px]: string[]) => [value, uiText(word), uiText(where), px],
  };
  /** Форма последней сверки (списки на новом языке сужаются по ней) */
  let listForm: StudioForm | null = null;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.tail.run;
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('.ap-foot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const slotKeys = (form: StudioForm | null | undefined) => postFields(form).photos.map(field => field.key);
  /** Заявка заказа: сервиса, а пока он не проверил владельца — подсмотренная при открытии */
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Итог на экране: подтверждённая правка слайда (подзаказ) или заказ сервиса */
  const shownOrder = (f: Frame | null): SpeechOrder | null => {
    const order = f?.svc.get().order ?? null;
    if (!f || order?.status !== 'done') return order;
    return f.edit?.root === order.id ? (f.edit.core.shown() ?? order) : order;
  };
  /** Вид S для входов */
  const sView = (): PostView => ({ text: S.text, sty: h.sel().key, n: S.n, ar: S.ar, tone: S.tone });

  /* ---- свои фото лотка ---- */
  /** S.photos — записи лотка моста, у которых есть что показать (превью повтора, пока читается, не показывается) */
  function photosOf(f: Frame): PostPhoto[] {
    return (f.tray?.list() ?? []).filter(item => !!item.url).map(item => ({ url: item.url, name: item.name, pos: 40, f: '', own: true, id: item.id }));
  }
  /** Лоток моста изменился: S.photos заново, рамка и всё, что зависит от фото (предпросмотр, лента, поле, кнопка) */
  function trayChanged(f: Frame) {
    if (cur() !== f) return;
    const list = photosOf(f);
    h.setPhotos(list);
    if (list.length) S.ph = true;
    if (!onForm()) return;
    h.photosChanged();
  }
  /** Права на фото не подтверждены, а фото повтора стоят в лотке: кнопка подтверждает их (лист согласия без файла) */
  const rightsNeeded = (f: Frame) => !f.svc.get().rights && !!f.tray?.list().some(item => item.state === 'ready');
  function confirmRights(f: Frame) {
    f.consent?.close();
    f.consent = openConsentSheet({
      rights: () => f.svc.get().rights,
      locked: () => f.svc.derive().locked,
      setRights: on => f.svc.setters.rights(on),
      confirm: true,
      texts: RIGHTS,
      onContinue: () => {
        f.consent = null;
        if (cur() === f) h.foot.sync();
      },
      onClose: () => {
        f.consent = null;
      },
    });
  }

  /* ---- готовность ---- */
  function missing(): Missing | null {
    const f = cur();
    if (!f) return 'load';
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return 'load';
    if (s.integrityError || !s.identity || s.identity.appId !== f.app.id) return 'blocked';
    /* форма не загрузилась или устарела: кнопка «Обновить форму» (recovery) */
    if (!s.form) return s.error ? '' : 'load';
    if (s.versionStale) return '';
    if (!slideContractSupported(s.form, 'post')) return 'off';
    if (!s.form.available) return 'unavailable';
    /* чинить, а не создавать: «Продолжить загрузку», «Подтвердите права на фото» — кнопка активна */
    if (recovery(f)) return '';
    return null;
  }
  function ready(): Ready {
    const f = cur();
    if (!f) return 'upload';
    /* фото ещё грузятся или читаются превью повтора */
    return f.bridge.pending() ? 'upload' : '';
  }
  /** Что починить вместо создания: форма не загрузилась или устарела — перечитать; загрузка не подтверждена — продолжить тем же id;
      фото повтора без подтверждённых прав — подтвердить */
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    if (s.form && rightsNeeded(f)) return () => confirmRights(f);
    return null;
  }
  function label(): string | null {
    const f = cur();
    if (!f) return null;
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return uiText('Продолжить проверку');
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
    if (s.form && rightsNeeded(f)) return uiText('Подтвердите права на фото');
    if (s.form?.chargeable === false) return uiText('Создать карусель');
    return null;
  }
  /** Скидка «дешевле» формы — только для той цены, для которой она посчитана (цена формы, D11) */
  function cheapOffer(f: Frame | null) {
    if (!f) return null;
    const { s, d } = live(f);
    const offer = studioCheapOffer(s.form);
    return offer && s.form && d.price === s.form.expected_price ? offer : null;
  }

  /** Цена карусели: платная форма — прайс сервера, иначе карточка каталога */
  const postPrice = () => {
    const f = cur() ?? frame;
    return studioAppUnit(toolPrice(f?.app.price).amount, f?.svc.get().form);
  };
  /* ---- сверка формы с сервисом (каждый зафиксированный вид) ---- */
  /** Стили, тоны, форматы и количества шаблона — только опубликованные (порядок шаблона); поля нет — как в шаблоне */
  function narrow(form: StudioForm | null) {
    listForm = form;
    const f = postFields(form);
    const cut = <T>(list: T[], all: T[], keep: (item: T) => boolean) => {
      const next = all.filter(keep);
      list.splice(0, list.length, ...(next.length ? next : all));
    };
    /* копии на языке профиля (ключи и id те же) */
    cut(h.STY, ALL.sty.map(said.sty), item => !f.style || f.style.values.some(value => value === item.key || value === String(item.id)));
    cut(h.TONES, ALL.tones.map(said.tone), item => !f.tone || f.tone.values.includes(item[0]));
    cut(h.FMTS, ALL.fmts.map(said.fmt), item => !f.format || f.format.values.includes(item[0]));
    cut(h.NS, ALL.ns, n => !f.slides || f.slides.values.includes(String(n)));
    h.THEMES.splice(0, h.THEMES.length, ...ALL.themes.map(theme => uiText(theme)));
    /* лента строит свой список заново; выбор вне опубликованного — первый опубликованный */
    S.list = [];
    if (!h.STY.some(item => String(item.id) === String(S.sty))) S.sty = h.STY[0].id;
    if (!h.TONES.some(item => item[0] === S.tone)) S.tone = h.TONES[0][0];
    if (!h.FMTS.some(item => item[0] === S.ar)) S.ar = h.FMTS[0][0];
    if (!h.NS.includes(S.n)) S.n = h.NS.includes(6) ? 6 : h.NS[0];
  }
  /** Входы → S (повтор, «Изменить», заявка родной формы, значения по умолчанию) */
  function applyView(view: PostView) {
    S.text = view.text;
    const st = view.sty ? h.STY.find(item => item.key === view.sty) : undefined;
    if (st) S.sty = st.id;
    if (view.n !== undefined && h.NS.includes(view.n)) S.n = view.n;
    if (view.ar && h.FMTS.some(item => item[0] === view.ar)) S.ar = view.ar;
    if (view.tone && h.TONES.some(item => item[0] === view.tone)) S.tone = view.tone;
  }
  /** Фото повтора: id слотов во входах, которых нет в лотке, — превью (один раз на набор); записи лотка, которых больше нет во входах
      (сервис снял загрузку), уходят */
  function photosSync(f: Frame) {
    const tray = f.tray;
    if (!tray) return;
    const { s, d } = live(f);
    const ids = tray.keys().map(key => s.inputs[key]).filter((id): id is string => !!id);
    const list = tray.list();
    const known = (id: string) => list.some(item => item.uploadId === id || item.pendingId === id);
    if (ids.length && !ids.every(known) && f.restored !== ids.join()) {
      f.restored = ids.join();
      void tray.restore(ids).finally(() => {
        if (cur() === f && h.mounted()) changed(f);
      });
      return;
    }
    if (d.locked || f.bridge.pending()) return;
    for (const item of list) if (item.state === 'ready' && item.uploadId && !ids.includes(item.uploadId)) tray.remove(item.id);
  }
  /** Раскладка формы на экране: опубликованные стили, тоны, форматы и количества (без формы — пусто) */
  const layoutOf = (f: Frame) =>
    f.svc.get().form ? JSON.stringify([accountLocale(), h.STY.map(item => item.id), h.TONES.map(item => item[0]), h.FMTS.map(item => item[0]), h.NS]) : '';
  /** Сверка без отрисовки: первая форма (пределы, списки, лоток), значения по умолчанию, входы → S.
      → входы изменили S (форму надо нарисовать заново) */
  function prime(f: Frame): boolean {
    const { s, d } = live(f);
    const form = s.form;
    if ((form || s.error || s.integrityError) && f.settled) f.settled();
    if (form && f.form !== form) {
      /* первая сверка этой формы: пределы лотка и темы, опубликованные списки */
      f.form = form;
      const fields = postFields(form);
      const keys = slotKeys(form);
      const policy = keys[0] ? mediaPolicy(form, keys[0]) : null;
      h.setLimits({ files: keys.length, mb: policy ? policy.maxBytes / MB : 0, tmax: fields.topic?.max_length ?? 0 });
      narrow(form);
      f.tray = keys.length ? f.bridge.slots(keys) : null;
    }
    /* значения по умолчанию — только в опубликованные выборы и не поверх заявки (use-product-defaults.ts) */
    if (form && !d.locked && !s.intent) {
      const add = productDefaults(form, s.inputs, DEFAULTS);
      if (add.length)
        f.svc.setters.inputs(previous => {
          const next = { ...previous };
          for (const [key, value] of add) if (!next[key]) next[key] = value;
          return next;
        });
    }
    let repaint = false;
    const now = f.svc.get();
    if (form && (f.fresh || f.mirror.external(now.inputs))) {
      /* S только что сброшен (другой кадр рисовал экран) или входы изменило не зеркало: в S — если они не те, что S дал бы сам
         (запись моста о фото S не трогает) */
      if (f.fresh || !sameInputs(inputsOf(form, sView(), now.inputs), now.inputs)) {
        applyView(viewOf(form, now.inputs));
        repaint = true;
      }
      f.fresh = false;
      f.mirror.seen(now.inputs);
    }
    return repaint;
  }
  /** Форма на экран: сверка без отрисовки, затем одна отрисовка (её раскладка запоминается) */
  function paintForm(f: Frame) {
    prime(f);
    f.layout = layoutOf(f);
    h.paintPage();
  }
  function formSync(f: Frame) {
    const repaint = prime(f);
    const now = f.svc.get();
    if (now.form && onForm()) photosSync(f);
    const layout = layoutOf(f);
    if (layout !== f.layout || repaint) {
      f.layout = layout;
      if (onForm()) h.paintPage();
    }
    if (!onForm()) return;
    /* ошибка формы (не загрузилась, файлы повтора недоступны) — один раз в рамке */
    const error = now.integrityError ? '' : now.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.up.say(uiText(error));
    }
    h.foot.sync();
  }
  /** S.photos — те же записи, что в лотке (мост убрал или переставил запись без своего отклика) */
  function photosCheck(f: Frame) {
    const now = photosOf(f);
    if (now.length !== S.photos.length || now.some((photo, i) => photo.id !== S.photos[i]?.id || photo.url !== S.photos[i]?.url)) trayChanged(f);
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    photosCheck(f);
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту): спрашивается при каждом
      показе формы, если в памяти его нет; созданный или открытый заказ делает память устаревшей */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      first = shown && !f.formShown;
    f.formShown = shown;
    if (!first || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.foot.sync();
      });
  }

  /* ---- предпросмотр ---- */
  /** Колода предпросмотра на языке профиля (Q-CS4): по-русски — колоды шаблона (DECKS по теме), на других языках — общая колода GEN
      словарём: крючок — тема (своя или пример стиля), пункты пользы «Шаг N», подзаголовок крючка и призыв — по тону */
  const ru = () => accountLocale() === 'ru';
  function localDeck(topic: string, n: number, tone: string, v: number): PostSlide[] {
    return h.deckOf(h.GEN, topic, n, tone, v).map(slide =>
      slide.k === 'val'
        ? { ...slide, kk: uiFormat('Шаг {0}', slide.no), t: uiText(slide.t), b: uiText(slide.b) }
        : slide.k === 'hook'
          ? { ...slide, kk: uiText(slide.kk), b: uiText(slide.b) }
          : { ...slide, t: uiText(slide.t), b: uiText(slide.b) },
    );
  }
  /** Колода предпросмотра по теме, числу своих фото, количеству и тону */
  function previewDeck(text: string, photos: number, n: number, tone: string): PostSlide[] {
    const t = text.trim();
    if (!ru()) return localDeck(t || uiText((photos ? h.DECKS.pics : h.DECKS.run).topic), n, tone, 0);
    const deck = h.deckFor(t, photos) ?? h.GEN;
    return h.deckOf(deck, t || deck.topic, n, tone, 0);
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    /* тексты страницы заказа шаблона (T) — словарём профиля в момент показа (BotRun берёт их через val); подпись итога — по доставке */
    const given = (opts.T as Record<string, unknown>) ?? {};
    const T = Object.fromEntries(Object.entries(given).map(([key, value]) => [key, typeof value === 'string' ? () => uiText(value) : value]));
    const doneText = typeof given.doneP === 'string' ? given.doneP : '';
    const head = (opts.head as Record<string, unknown>) ?? {};
    const title = head.title;
    const foot = { ...((opts.foot as Record<string, unknown>) ?? {}) };
    /* подписи подвала шаблона (mod: «Изменить тему») — функции: BotRun берёт их через val при каждом показе */
    for (const [key, value] of Object.entries(foot))
      if (Array.isArray(value) && typeof value[2] === 'string') foot[key] = [value[0], value[1], () => uiText(value[2] as string)];
    /* действия итога и «Что дальше» — подписи словарём (BotRun вставляет их разметкой) */
    const labelled = (list: unknown) =>
      Array.isArray(list) ? list.map(item => (Array.isArray(item) && typeof item[2] === 'string' ? [item[0], item[1], esc(uiText(item[2]))] : item)) : list;
    const acts = opts.acts as ((r: BotRunRun) => unknown) | undefined,
      next = opts.next as ((r: BotRunRun) => unknown) | undefined;
    inst = create({
      ...opts,
      T: { ...T, doneP: () => doneP(shownOrder(cur() ?? frame), esc(uiText(doneText)), 'po') },
      head: { ...head, title: (r: BotRunRun) => uiText(String(typeof title === 'function' ? (title as (r: BotRunRun) => unknown)(r) : (title ?? ''))) },
      ...(acts ? { acts: (r: BotRunRun) => labelled(acts(r)) } : {}),
      ...(next ? { next: (r: BotRunRun) => labelled(next(r)) } : {}),
      card: (r: BotRunRun) => card(r),
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      foot: chatFoot(serverFoot(foot, 'po'), 'po'),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => cur()?.svc.derive().price ?? 0,
      /* повтор заявки и «Попробовать ещё раз» решает сервис: форма в памяти им не нужна */
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) =>
        (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });

  /* ---- привязка кадра ---- */
  function bind(b: EngineBinding): EngineBound {
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) {
        frame.bridge.cancel();
        frame.consent?.close();
        frame.gallery.release();
        frame.edit?.core.dispose();
      }
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      ready: () => mine.first,
      back: () => cur() === mine && server.back(),
    };
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    let settle!: () => void;
    const first = new Promise<void>(resolve => (settle = resolve));
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      tray: null,
      form: null,
      layout: '',
      restored: '',
      said: '',
      consent: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      first,
      settled: () => {
        settle();
        f.settled = undefined as unknown as () => void;
      },
    });
    f.mirror = createInputsMirror(b.svc);
    f.peek = null;
    f.fresh = true;
    f.edit = null;
    f.drafts = new Set();
    f.gallery = createResultGallery(SLIDE_LIMITS);
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.tail,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => (cur() === f ? paintForm(f) : h.paintPage()),
      tip: (el, text) => tip(el, text),
      onDone: () => onDone(f),
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      say: guard((text: string) => h.up.say(text)),
      put: guard(() => trayChanged(f)),
      drop: guard(() => trayChanged(f)),
      update: guard(() => trayChanged(f)),
      consentWith: () => ({ texts: RIGHTS }),
    });
    return f;
  }

  const server: PostServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          /* кнопка чинит («Обновить форму», «Продолжить загрузку»), а не создаёт: «Не хватает · Пополнить» её не заслоняет */
          if (!f || recovery(f)) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, true);
        },
      }),
    tx: (template, ...values) => uiFormat(template, ...values),
    /* вход экрана — входы сервиса (повтор, «Изменить»): в setup шаблона ничего не идёт (и шаблона «Ночной рынок» тоже, Q-CS10), S ставит сверка формы */
    entry: () => ({}),
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.paintPage();
        return;
      }
      if (painted !== f) {
        /* другой кадр или новый сервис под прежним ключом: фото, тема, выборы, скидка и хвост прежнего сюда не переходят */
        h.liveStop();
        h.sheetClose();
        Object.assign(S, { sty: fresh.sty, n: fresh.n, ar: fresh.ar, tone: fresh.tone, text: '', photos: [], ph: false, cheap: false, sh: null, app: null, list: [], pv: 0, v: 0 });
        rebuild();
        painted = f;
        f.layout = '';
        f.form = null;
        f.fresh = true;
      }
      /* открытие на форме — новый показ кнопки: остаток спрашивается, если его нет в памяти */
      f.formShown = false;
      h.setup(o);
      h.setPhotos(photosOf(f));
      const root = h.root();
      if (h.tail.run && root) {
        h.tail.show(root);
        changed(f);
        return;
      }
      f.peek = peekIntent(f.app.id);
      /* форма рисуется один раз (paintForm водителя): сверка перерисует её, только если раскладка или входы станут другими */
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { where?: unknown; go?: unknown };
      /* скидку «Сделать дешевле» публикует форма, и она посчитана для текущей цены: без неё у подвала нет ни цены, ни действия скидки;
         цена со скидкой — цена формы (не BotCore.cheapPrice) */
      return {
        /* где водяной знак (лист «Не хватает Circle») — словарём профиля в момент показа */
        where: () => uiText(String(cheap.where ?? '')),
        get price() {
          return cheapOffer(cur()) ? () => server.price(true) : undefined;
        },
        get go() {
          return cheapOffer(cur()) ? cheap.go : undefined;
        },
        get percent() {
          return cheapOffer(cur())?.percent;
        },
      } as unknown as T;
    },
    missing,
    ready,
    missText(kind) {
      const f = cur();
      const s = f?.svc.get();
      switch (kind) {
        case 'load':
          return uiText('Проверяем доступность');
        case 'blocked':
          return uiText(s?.integrityError || 'Проверяем доступность');
        case 'off':
          return uiText('Этот вариант пока недоступен');
        case 'unavailable':
          return uiText('Создание сейчас недоступно');
        case 'upload':
          return uiText('Загружаем фото');
        default:
          return '';
      }
    },
    label,
    price(cheap) {
      const f = cur();
      if (!f) return 0;
      const offer = cheap ? cheapOffer(f) : null;
      return offer ? offer.price : f.svc.derive().price;
    },
    unit() {
      const amount = postPrice();
      return amount !== null && amount > 0 ? core().html.unit(amount, uiText('за карусель')) : '';
    },
    perPost() {
      const amount = postPrice();
      return amount !== null ? cNum(amount) : '—';
    },
    commit() {
      const f = cur();
      const form = f?.svc.get().form;
      /* S ещё не принял входы этой формы (первая сверка впереди): писать нечего — иначе выборы шаблона легли бы поверх повтора */
      if (!f || !form || f.fresh || f.form !== form) return;
      f.mirror.commit(previous => inputsOf(form, sView(), previous));
    },
    chips: list => list.map(([label, topic]) => [uiText(label), uiText(topic)]),
    prevDeck(st) {
      if (ru()) return null;
      const t = S.text.trim();
      const example = S.photos.length ? h.DECKS.pics : (h.DECKS[st.demo] ?? h.DECKS.run);
      return localDeck(t || uiText(example.topic), S.n, S.tone, S.v);
    },
    youPic: () => selfPicture(),
    me() {
      const user = (window as unknown as { BotServerMe?: { user?: { first_name?: string; last_name?: string | null; username?: string | null; photo_url?: string | null } } })
        .BotServerMe?.user;
      const first = user?.first_name ?? '',
        last = user?.last_name ?? '';
      return {
        first,
        last,
        name: [first, last].filter(Boolean).join(' ') || uiText('Без имени'),
        user: user?.username ?? '',
        photo: safeMediaUrl(user?.photo_url),
        initials: ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?',
      };
    },
    upOpts: () => ({
      accept: ACCEPT,
      /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Можно до N фото», «N из M») — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
      onPick(files, at) {
        const f = cur();
        if (!f || !files.length) return;
        if (!f.tray) {
          h.up.say(uiText('Проверяем доступность'));
          return;
        }
        void f.tray.pickSlots(files, at ?? undefined);
      },
    }),
    pick(files) {
      server.upOpts().onPick(files);
    },
    remove(i) {
      const f = cur();
      const id = S.photos[i]?.id;
      if (!f?.tray || !id) return;
      f.tray.remove(id);
      trayChanged(f);
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    start(btn) {
      const f = cur();
      if (!f) return;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.tail.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (h.foot.poor()) {
        h.foot.poorOpen();
        return;
      }
      h.tail.start();
    },
    /* снимок входа заказа (r.snap шаблона): из тела заявки (подсмотренной), без неё — из S; свои фото — только те, что ушли в заказ;
       колода — предпросмотр шаблона по теме (рамка этапов), подпись — итога сервера */
    snap() {
      const f = cur();
      const s = f?.svc.get();
      const it = intentOf(f);
      const view = it ? viewOfInputs(s?.form, it.body.inputs as Record<string, unknown>) : sView();
      const sty = (view.sty ? h.STY.find(item => item.key === view.sty) : undefined) ?? h.sel();
      const n = view.n ?? S.n;
      const tone = view.tone ?? S.tone;
      const ids = it ? photoIds(s?.form, it.body.inputs as Record<string, unknown>) : null;
      const photos = (f?.tray?.list() ?? [])
        .filter(item => !!item.url && (ids ? !!item.uploadId && ids.includes(item.uploadId) : true))
        .map(item => ({ url: item.url, name: item.name, pos: 40, f: '', own: true, id: item.id }));
      const text = view.text.trim();
      return {
        sty: { ...sty },
        text,
        n,
        ar: view.ar ?? S.ar,
        tone,
        photos,
        cheap: !!(it ? it.body.cheap : S.cheap),
        v: 0,
        app: null,
        deck: previewDeck(text, ids ? ids.length : photos.length, n, tone),
        cap: '',
      };
    },
    cell(r, i) {
      const f = cur() ?? frame;
      if (f?.drafts.has(i)) return null;
      const snap = r.snap as unknown as PostSnap;
      const deck = (snap.deck ?? []) as PostSlide[];
      const k = deck[i]?.k ?? 'val';
      const item = f ? f.gallery.items(shownOrder(f))[i] : undefined;
      const img = item ? `<img src="${esc(item.url)}" alt="" draggable="false" style="${IMG_STYLE}">` : '';
      return `<div class="po-rs${i === (r as BotRunRun & { cur?: number }).cur ? ' cur' : ''}" data-po-rs="${i}" role="group" aria-label="${esc(uiFormat('Слайд {0} из {1}', i + 1, deck.length))}"><div class="po-sl s-${esc(String(snap.sty?.key ?? 'min'))} k-${esc(k)}">${img}</div></div>`;
    },
    canEdit(r, i) {
      const f = cur();
      if (!f || r.st !== 'done') return false;
      const el = h.root()?.querySelector<HTMLElement>(`[data-po-rs="${i}"]`) ?? null;
      const order = shownOrder(f);
      const item = f.gallery.items(order)[i];
      const slide = item ? productResult(order).slides.find(entry => entry.id === item.index) : undefined;
      const root = f.svc.get().order;
      const edit = root ? editOf(f, root) : null;
      const view = edit?.view();
      if (!slide || !edit || !view || view.blocked || !view.chain || (r.snap as unknown as ResultSnap).res !== galleryKey(order)) {
        tip(el, 'Этот слайд нельзя изменить');
        return false;
      }
      if (view.busy || view.chain.pending) {
        tip(el, view.chain.pending && !view.chain.pending.child ? 'Правка не подтверждена. Продолжите проверку того же запроса.' : 'Перерисовываем слайд…');
        return false;
      }
      /* слайд становится разметкой шаблона с текстом сервера: правят текст, а не картинку */
      if (!f.drafts.has(i)) {
        f.drafts.add(i);
        if (el) el.outerHTML = h.rsHTML(r, i);
      }
      return true;
    },
    edited(r, i, quiet) {
      const f = cur();
      if (!f || quiet) return;
      const snap = r.snap as unknown as ResultSnap;
      const was = snap.deck[i];
      const now = ((r as BotRunRun & { deck?: PostSlide[] }).deck ?? snap.deck)[i];
      const order = shownOrder(f);
      const item = f.gallery.items(order)[i];
      const root = f.svc.get().order;
      const edit = root ? editOf(f, root) : null;
      if (!was || !now || !item || !edit) return;
      const title = String(now.t ?? '').trim(),
        body = String(now.b ?? '').trim();
      if (title === String(was.t ?? '').trim() && body === String(was.b ?? '').trim()) {
        /* без изменений: слайд снова PNG сервера */
        f.drafts.delete(i);
        cellPaint(r, i);
        return;
      }
      const el = h.root()?.querySelector<HTMLElement>(`[data-po-rs="${i}"]`) ?? null;
      el?.classList.add('busy');
      /* PATCH через ядро правки (ключ — в хранилище до транспорта); новый PNG придёт подзаказом (onDone → resultPaint) */
      void edit.save(item.index, title, body).then(ok => {
        el?.classList.remove('busy');
        if (!ok) tip(el, edit.view().error || 'Правка не подтверждена. Продолжите проверку того же запроса.');
      });
    },
    click(d, el, r) {
      const f = cur();
      if (d.poRetry !== undefined || d.poRchk !== undefined || d.poFix !== undefined) {
        if (!f || f.driver.busy()) return true;
        if (d.poRetry !== undefined) h.tail.retry();
        else if (d.poRchk !== undefined) void f.driver.check(el);
        else void f.driver.leave(() => h.tail.exit(() => h.paintPage()), false, '');
        return true;
      }
      if (d.poChat !== undefined) {
        chatClick({ order: f?.svc.get().order, el, text: 'Тут откроется тема чата, куда бот прислал карусель', tip: (at, text) => tip(at, text) });
        return true;
      }
      if (d.poSettings !== undefined) {
        openSettings();
        return true;
      }
      if (!f || !r) return false;
      /* «Ещё вариант» — к форме с теми же настройками, без заказа (D10, как родной итог) */
      if (d.poAgain !== undefined) {
        if (r.st === 'done') server.leave(() => h.tail.exit(() => h.paintPage()));
        return true;
      }
      /* «Сделать рилс» — экран «Рилс», как у родного итога (ProductOrderResult) */
      if (d.poReels !== undefined) {
        if (r.st === 'done') openStudioTarget({ screen: 'reels' });
        return true;
      }
      return false;
    },
    leave(go) {
      const f = cur();
      if (!f) return;
      void f.driver.leave(go, false, '');
    },
    save(btn) {
      const f = cur();
      const order = shownOrder(f);
      if (!f || order?.status !== 'done') return;
      const items = f.gallery.items(order);
      const saveBtn = (window.BotCore as unknown as { saveBtn?(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: object): void }).saveBtn;
      if (!items.length || !saveBtn) {
        tip(btn, 'Слайды ещё загружаются');
        return;
      }
      const ui = h.ui();
      /* несколько слайдов — архив итога (owner-protected путь заказа), один — сам файл */
      saveBtn(
        btn,
        async () =>
          items.length > 1 && archiveUrl(order) ? { data: await archive(order), name: 'steos-post.zip' } : { data: items[0].blob, name: items[0].name || 'steos-post.png' },
        { buzz: () => ui.buzz(), tip: (el: Element, text: string) => ui.tip(el, text) },
      );
    },
    steps: () => h.runSteps().map(([key, icon, run, done]) => [key, icon, uiText(run), uiText(done)]),
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: FAIL,
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на слайдах' });
    },
    back() {
      const f = cur();
      const r = h.tail.run as (BotRunRun & { ed?: number | null }) | null;
      if (r && r.ed != null) {
        /* правка слайда: заканчивается без сохранения, черновик остаётся на слайде */
        h.edEnd(true);
        return true;
      }
      if (f?.consent?.isOpen()) {
        f.consent.close();
        return true;
      }
      const sh = S.sh;
      if (sh) {
        if (sh.view === 'card' && sh.from === 'grid') h.sheetView('grid');
        else h.sheetClose();
        return true;
      }
      if (h.set.isOpen) {
        h.set.close();
        return true;
      }
      return false;
    },
  };

  /** Карточка заказа итога (runWhat шаблона на языке профиля): стиль, слайдов, формат, тон, свои фото; тема; текст поста — когда он пришёл */
  function card(r: BotRunRun): string {
    const snap = r.snap as unknown as PostSnap;
    const fmt = h.FMTS.find(item => item[0] === snap.ar) ?? ALL.fmts.find(item => item[0] === snap.ar) ?? ALL.fmts[0];
    const tone = ALL.tones.find(item => item[0] === snap.tone) ?? ALL.tones[0];
    const what = [
      uiFormat('Стиль «{0}»', uiText(snap.sty?.n ?? '')),
      slidesText(snap.deck?.length || snap.n),
      uiText(fmt[1]).toLowerCase(),
      uiFormat('тон {0}', uiText(tone[1]).toLowerCase()),
      ...(snap.photos?.length ? [uiFormat('{0} фото', snap.photos.length)] : []),
    ].join(' · ');
    const q = (facade as unknown as { q?(meta: string, text: string): string }).q;
    return (r.st === 'done' && snap.cap ? h.capHTML(r) : '') + (q ? q(what, snap.text || uiText('Текст по вашим фото')) : '');
  }

  /* ---- итог: слайды сервера ---- */
  /** Правка слайда на исходный готовый заказ (одна на заказ; цепочка — та же, что у родного итога) */
  function editOf(f: Frame, order: SpeechOrder): SlideEdit | null {
    const owner = f.svc.get().identity?.owner;
    if (!owner || order.status !== 'done') return null;
    if (f.edit?.root !== order.id) {
      f.edit?.core.dispose();
      const core = createSlideEdit({
        owner,
        rootId: order.id,
        root: () => (f.svc.get().order?.id === order.id ? f.svc.get().order : order),
        onChange: () => {
          if (cur() === f) onDone(f);
        },
      });
      f.edit = { root: order.id, core };
      void core.load();
    }
    return f.edit.core;
  }
  /** Заказ готов (каждая сверка): правка и слайды итога; пришли — слайды встают на место */
  function onDone(f: Frame) {
    const order = f.svc.get().order;
    if (!order || order.status !== 'done') return;
    editOf(f, order);
    const shown = shownOrder(f);
    void f.gallery.load(shown).then(items => {
      if (items && cur() === f) resultPaint(f, shown, items);
    });
  }
  /** Ячейка ленты итога на месте (PNG сервера или пустая рамка) */
  function cellPaint(r: BotRunRun, i: number) {
    const el = h.root()?.querySelector<HTMLElement>(`#poRt [data-po-rs="${i}"]`);
    const html = server.cell(r, i);
    if (!el || html === null) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const next = tmp.firstElementChild as HTMLElement | null;
    if (!next) return;
    next.classList.toggle('cur', el.classList.contains('cur'));
    if (el.outerHTML !== next.outerHTML) el.replaceWith(next);
  }
  /** Слайды итога по файлам сервера: колода снимка — тексты слайдов сервера (тот же порядок), подпись — текст поста сервера;
      новый набор (заказ или ревизия правки) перерисовывает итог, тот же — только ячейки */
  function resultPaint(f: Frame, order: SpeechOrder | null, items: GalleryItem[]) {
    const r = h.tail.run as (BotRunRun & { deck?: PostSlide[] | null; ed?: number | null; cur?: number }) | null;
    if (!r || r.st !== 'done' || !h.mounted() || order !== shownOrder(f)) return;
    const snap = r.snap as unknown as ResultSnap;
    const key = galleryKey(order);
    if (snap.res !== key) {
      const shape = productResult(order);
      const last = items.length - 1;
      snap.deck = items.map((item, i) => {
        const slide = shape.slides.find(entry => entry.id === item.index);
        const k = i === 0 ? 'hook' : i === last ? 'cta' : 'val';
        return { k, kk: '', t: slide ? slide.title || slide.text : '', b: slide?.body ?? '', ...(k === 'val' ? { no: i } : {}) };
      });
      snap.n = items.length;
      snap.cap = shape.caption;
      snap.res = key;
      r.deck = null;
      r.ed = null;
      if (typeof r.cur === 'number' && r.cur >= items.length) r.cur = 0;
      f.drafts.clear();
      h.tail.refresh();
      return;
    }
    items.forEach((_, i) => {
      if (!f.drafts.has(i)) cellPaint(r, i);
    });
  }

  /* ---- смена языка профиля (i18n: событие steos:locale): открытый экран рисуется заново ---- */
  /** Подписи этапов идущего заказа — строки на момент создания хвоста: им — подписи нового языка (ключи этапов те же) */
  function relabel(r: BotRunRun) {
    const next = server.steps();
    for (const step of r.steps) {
      const same = next.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  document.addEventListener('steos:locale', () => {
    const f = cur();
    const r = h.tail.run;
    if (r) relabel(r);
    /* стили, тоны, форматы и темы — копиями на новом языке */
    narrow(listForm);
    if (!f || !h.mounted()) return;
    const root = h.root();
    if (r && root) h.tail.show(root);
    else h.paintPage();
    changed(f);
  });
  /* новый снимок Me (пополнение) уже положил остаток в память кнопки (tool-helpers): подвал формы — по нему */
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.foot.sync();
  });

  instance = { bind };
  return server;
}

/** ENGINES.post.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindPostEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Экран «post» реестра движков студии: движок шаблона window.BotPost, атрибуты data-po-*, без правой панели, знак заказа «post» */
export const POST_ENGINE: EngineScreen = { global: 'BotPost', prefix: 'po', side: false, kind: 'post', bind: bindPostEngine };

export function registerPostEngine() {
  ENGINES.post = POST_ENGINE;
}
